import { StarRating } from '@/components/StarRating';
import { ExerciseDTO, TrainingHistory, TrainingHistoryEntry } from '@/generated';
import { useDeckExercisesQuery, useListUserDecksQuery } from '@/hooks/query-hooks';
import { Box, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { DotsItemSymbolProps } from '@nivo/core';
import { Point, PointTooltipProps, ResponsiveLine } from '@nivo/line';
import moment, { Moment } from 'moment';
// import 'moment/locale/de';

export enum FilterMode {
  time,
  volume,
}

type StatProps = {
  date: Moment | null;
  trainingHistory?: TrainingHistory;
  selectedEntry?: TrainingHistoryEntry;
  setSelectedEntry: (entry?: TrainingHistoryEntry) => void;
  filterMode: FilterMode;
};

type PointProps = { id: string; data: Array<{ x: string; y: number; entryId: string }> };

const CustomPointSymbol = (
  props: DotsItemSymbolProps<
    Point<{
      id: string;
      data: Array<{
        x: string;
        y: number;
        entryId: string;
      }>;
    }>
  > & { selectedItemId?: string },
) => {
  const isSelected = props.selectedItemId === (props.datum as any)?.entryId;
  return <circle cx={1} cy={2} r={6} fill={isSelected ? 'red' : props.borderColor} stroke={props.borderColor} strokeWidth={isSelected ? 3 : 1} />;
};

const TimeLineTooltip = ({ point }: PointTooltipProps<PointProps>) => {
  const { data, color } = point;

  return (
    <Paper elevation={6} sx={{ width: 220, px: 1.25, py: 1, border: 1, borderColor: color }}>
      <Typography variant="body2">Training on {moment(data.x).format('DD.MM HH:mm')}</Typography>
      <Typography variant="body2">Duration: {data.y.toFixed(3)} min</Typography>
    </Paper>
  );
};

const VolumeLineTooltip = ({ point }: PointTooltipProps<PointProps>) => {
  const { data, color } = point;

  return (
    <Paper elevation={6} sx={{ width: 220, px: 1.25, py: 1, border: 1, borderColor: color }}>
      <Typography variant="body2">Training on {moment(data.x).format('DD.MM HH:mm')}</Typography>
      <Typography variant="body2">Volume Points: {data.y.toFixed(0)}</Typography>
    </Paper>
  );
};

export const OverallStats = (props: StatProps) => {
  const { trainingHistory, selectedEntry, setSelectedEntry, date, filterMode } = props;
  const { decks } = useListUserDecksQuery(true);
  let bySetId: Array<PointProps> = [];
  const { exercises } = useDeckExercisesQuery();
  trainingHistory?.historyEntries
    .filter((he) => timeFilter(he, date))
    .forEach((he) => {
      // if (filterMode === FilterMode.intensity) intensityBuilder(he, exercises, bySetId);
      if (filterMode === FilterMode.time) durationBuilder(he, bySetId);
      else volumeBuilder(he, exercises, bySetId);
    });

  bySetId = bySetId.map((b) => ({ ...b, id: decks?.find((s) => s.id === b.id)?.name ?? b.id }));

  return (
    <Paper variant="outlined" sx={{ width: '100%', minWidth: 0, p: 1 }}>
      <Box sx={{ height: 300, width: '100%', minWidth: 0 }}>
        <ResponsiveLine /* or Line for fixed dimensions */
            data={bySetId ?? []}
            pointSymbol={(p) => <CustomPointSymbol {...p} selectedItemId={selectedEntry?.entryId} />}
            tooltip={(t) => {
              if (filterMode === FilterMode.time) return <TimeLineTooltip {...t} />;
              else return <VolumeLineTooltip {...t} />;
            }}
            margin={{ top: 50, right: 110, bottom: 80, left: 60 }}
            xScale={{ type: 'time', format: '%Y-%m-%dT%H:%M:%S.%LZ', precision: 'minute' }}
            yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false, reverse: false }}
            onClick={(p) => {
              const entryId = (p as any).data?.entryId;
              const selected = trainingHistory?.historyEntries.find((e) => e.entryId === entryId);
              setSelectedEntry(selected);
            }}
            axisBottom={{
              tickPadding: 12,
              tickRotation: 45,
              format: '%d %b',
              tickValues: 'every day',
              legend: 'Days',
              legendOffset: 70,
              legendPosition: 'middle',
              style: {},
            }}
            gridYValues={1}
            colors={{ scheme: 'dark2' }}
            axisLeft={{ legend: getYLegendName(filterMode), legendOffset: -50, tickValues: 8 }}
            pointSize={10}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'seriesColor' }}
            pointLabelYOffset={-12}
            enableTouchCrosshair={true}
            useMesh={true}
            theme={{ text: { fill: 'white' } }}
            legends={[
              {
                anchor: 'bottom-right',
                direction: 'column',
                translateX: 100,
                itemWidth: 80,
                itemHeight: 22,
                symbolShape: 'circle',
              },
            ]}
        />
      </Box>
    </Paper>
  );
};

export const TrainingRating = (props: { selectedEntry: TrainingHistoryEntry }) => {
  const effort = props.selectedEntry.rating?.effort;
  const enjoyment = props.selectedEntry.rating?.enjoyment;
  const note = props.selectedEntry.rating?.note?.trim();
  const hasEffort = effort !== undefined && effort > 0;
  const hasEnjoyment = enjoyment !== undefined && enjoyment > 0;

  return (
    <Paper
      variant="outlined"
      sx={{ position: 'relative', width: '100%', minWidth: 0, height: '100%', px: 2, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'center' }}
    >
      <Typography
        variant="caption"
        sx={{ position: 'absolute', top: -10, left: 12, px: 0.75, color: 'text.secondary', backgroundColor: 'background.paper' }}
      >
        Rating
      </Typography>
      <Stack direction="row" sx={{ width: '100%', minWidth: 0, gap: 2, alignItems: 'center' }}>
        {hasEffort || hasEnjoyment ? (
          <Stack direction="row" sx={{ gap: 2, flexShrink: 0 }}>
            {hasEffort && (
              <Stack direction="row" sx={{ gap: 0.75, alignItems: 'center' }}>
                <Typography variant="body2">Effort</Typography>
                <StarRating rating={effort} />
              </Stack>
            )}
            {hasEnjoyment && (
              <Stack direction="row" sx={{ gap: 0.75, alignItems: 'center' }}>
                <Typography variant="body2">Enjoyment</Typography>
                <StarRating rating={enjoyment} />
              </Stack>
            )}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No rating
          </Typography>
        )}
        {note && (
          <Stack sx={{ pl: 2, borderLeft: 1, borderColor: 'divider', minWidth: 0, flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Note
            </Typography>
            <Tooltip title={note} arrow placement="bottom-start">
              <Typography variant="body2" noWrap sx={{ cursor: 'help' }}>
                {note}
              </Typography>
            </Tooltip>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

const getYLegendName = (mode: FilterMode) => {
  if (mode === FilterMode.time) return 'Minutes';
  else return 'Volume';
};

const durationBuilder = (hm: TrainingHistoryEntry, bySetId: Array<PointProps>) => {
  let setItem = bySetId.find((s) => hm.deckId === s.id);
  if (!setItem) {
    setItem = { id: hm.deckId, data: [] };
    bySetId.push(setItem);
  }
  setItem.data.push({ x: hm.trainingDateIsoString, y: hm.completionTime / 1000 / 60, entryId: hm.entryId ?? '' });
};

const volumeBuilder = (hm: TrainingHistoryEntry, allExercises: ExerciseDTO[] | undefined, bySetId: Array<PointProps>) => {
  let setItem = bySetId.find((s) => hm.deckId === s.id);
  if (!setItem) {
    setItem = { id: hm.deckId, data: [] };
    bySetId.push(setItem);
  }
  const configs = hm.completedTraining.rounds.flatMap((r) => r.exercises.flatMap((e) => e));
  const volume = configs.reduce((p, c) => {
    const ex = allExercises?.find((e) => e.id === c?.exerciseId);
    if (!c) return p;
    const secondaryValue = ex?.config.trackPerSide ? (c.completionData.completedSecondaryValue ?? 0) : 0;
    return (
      p +
      (c.completionData.completedPrimaryValue ?? 0) * (c.completionData.completedPrimaryWeight ?? 1) +
      secondaryValue * (c.completionData.completedSecondaryWeight ?? c.completionData.completedPrimaryWeight ?? 1)
    );
  }, 0);

  setItem.data.push({ x: hm.trainingDateIsoString, y: volume, entryId: hm.entryId ?? '' });
};

// const intensityBuilder = (hm: TrainingHistoryEntry, allExercises: ExerciseDTO[] | undefined, bySetId: Array<PointProps>) => {
//   let setItem = bySetId.find((s) => hm.deckId === s.id);
//   if (!setItem) {
//     setItem = { id: hm.deckId, data: [] };
//     bySetId.push(setItem);
//   }
//   const configs = hm.completedTraining.rounds.flatMap((r) => r.exercises.flatMap((e) => e));
//   const volume = configs.reduce((p, c) => {
//     const ex = allExercises?.find((e) => e.id === c?.exerciseId);
//     if (!c) return p;
//     const secondaryValue = ex?.config.trackPerSide ? (c.completionData.completedSecondaryValue ?? 0) : 0;
//     return (
//       p +
//       (c.completionData.completedPrimaryValue ?? 0) * (c.completionData.completedPrimaryWeight ?? 1) +
//       secondaryValue * (c.completionData.completedSecondaryWeight ?? c.completionData.completedPrimaryWeight ?? 1)
//     );
//   }, 0);

//   const intensity = volume / (hm.completionTime / 1000);

//   setItem.data.push({ x: hm.trainingDateIsoString, y: intensity, entryId: hm.entryId ?? '' });
// };

const timeFilter = (he: TrainingHistoryEntry, date: moment.Moment | null) => {
  const trainingDate = moment(he.trainingDateIsoString);
  return trainingDate.month() === date?.month() && trainingDate.year() === date.year();
};
